import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { Box, Button, Flex, FlexColumn, Heading, Icon, Suspense, Text } from 'core/components';
import { Field, formConsumer, InputGroup, RadioGroup } from 'core/form';
import { ReactComponent as DockedIcon } from 'app/assets/icons/docked.svg';
import { ReactComponent as DrawerIcon } from 'app/assets/icons/drawer.svg';

import { computeUserLabelAccess, canAccessResource } from 'app/util/rbac/labelAccessUtils';
import { buildLandingPageTypeOptions } from 'app/util/landingPage/landingPage';
import { landingDisplayLabels } from 'app/util/landingPage/landingPageConstants';

const DockedItemRenderer = ({ label, type }) => (
  <Flex alignItems="center">
    {type === 'dock' && <DockedIcon style={{ width: 45, height: 45, marginRight: 8 }} />}
    {type === 'drawer' && <DrawerIcon style={{ width: 45, height: 45, marginRight: 8 }} />}

    {label}
  </Flex>
);

@inject('$auth', '$users', '$setup', '$savedViews', '$boards', '$rbac', '$dashboards')
@formConsumer
@observer
class UserInfo extends Component {
  componentDidMount() {
    const { $boards } = this.props;
    $boards.collection.fetch();
  }

  handleResetApiToken = () => {
    const { model, $users, form } = this.props;
    $users.resetUserApiToken(model.id).then((user_api_token) => {
      form.setValue('user_api_token', user_api_token);
    });
  };

  handleDisableTotp = () => {
    const { model, $users } = this.props;
    $users.disableUserTotps(model.id);
  };

  getLandingPageTypeOptions = (form) => {
    const { $auth, $savedViews, $boards, $rbac, $dashboards, model } = this.props;

    const boardAccess = computeUserLabelAccess({
      roleIds: model?.get('roles') || [],
      roleSetIds: model?.get('role_sets') || [],
      permissionKey: 'board::read',
      $rbac
    });
    const boardOptions = $boards.assignableBoards.map((board) => ({
      ...board.selectOptionFormat,
      disabled: !canAccessResource({ userAccess: boardAccess, resourceLabelIds: board.labelIds }),
      disabledTooltip: 'Assignee does not have access to this board'
    }));

    const dashAccess = computeUserLabelAccess({
      roleIds: model?.get('roles') || [],
      roleSetIds: model?.get('role_sets') || [],
      permissionKey: 'dashboards::read',
      $rbac
    });
    const disabledDashIds = dashAccess.all
      ? []
      : $dashboards.collection
          .get()
          .filter((d) => !canAccessResource({ userAccess: dashAccess, resourceLabelIds: d.labelIds || [] }))
          .map((d) => d.id);

    const savedViewAccess = computeUserLabelAccess({
      roleIds: model?.get('roles') || [],
      roleSetIds: model?.get('role_sets') || [],
      permissionKey: 'saved_views::read',
      $rbac
    });
    const savedViewOptions = $savedViews.collection.get().map((savedView) => ({
      ...savedView.selectOptionFormat,
      disabled: !canAccessResource({ userAccess: savedViewAccess, resourceLabelIds: savedView.labelIds || [] }),
      disabledTooltip: 'Assignee does not have access to this saved view'
    }));

    return buildLandingPageTypeOptions({
      currentType: form.getValue('defaultLandingPageType'),
      boardOptions,
      savedViewOptions,
      hasMarketIntel: $auth.hasPermission('marketIntel.enabled'),
      dashboardSelectorParams: {
        disabledIds: disabledDashIds,
        disabledTooltip: 'Assignee does not have access to this dashboard'
      },
      menuWidth: 'auto'
    });
  };

  onLandingPageTypeChange = () => {
    const { form } = this.props;
    form.getField('defaultLandingPageValue').init(undefined);
  };

  render() {
    const { model, $users, $setup, loading, form } = this.props;
    const isNew = model && model.isNew;
    const { totpEnabled } = (model && model.get(['totpEnabled'])) || {};
    const rolesetLandingPageType = model && model.get('rolesetLandingPageType');
    const rolesetLandingName = model && model.get('rolesetLandingName');

    // Get layout preferences from $setup
    const layoutSettings = $setup.getSettings('layout', {});
    const alwaysDockDrawers = layoutSettings.alwaysDockDrawers ?? false;
    const drawerLocation = layoutSettings.drawerLocation ?? 'right';

    return (
      <Suspense loading={loading}>
        <Field
          name="user_email"
          helpText={
            <>
              Changes will send a verification message to the updated address.
              <br />
              The new address will only appear after user has completed verification.
            </>
          }
          autoFocus
          large
        >
          <InputGroup width={300} />
        </Field>
        <Field name="user_full_name" large>
          <InputGroup width={300} />
        </Field>

        <Box borderTop="thin" my={2} />
        <Box mb={2}>
          <Field
            mb={0}
            name="defaultLandingPageType"
            large
            options={this.getLandingPageTypeOptions(form)}
            onChange={this.onLandingPageTypeChange}
          >
            <RadioGroup large />
          </Field>
          {rolesetLandingPageType && (
            <Flex alignItems="center" mt={1}>
              <Text small muted>
                <Icon icon="warning-sign" color="warning" mr={1} />
                Landing Page is configured via a Role Set (
                <Text as="span" fontWeight="bold">
                  {rolesetLandingName}
                </Text>
                {' as: '}
                <Text as="span" fontWeight="bold">
                  {landingDisplayLabels[rolesetLandingPageType] || rolesetLandingPageType}
                </Text>
                ) and supersedes the personal setting above.
              </Text>
            </Flex>
          )}
        </Box>

        {!isNew && (
          <Box borderTop="thin">
            <Flex alignItems="flex-end" pt={2}>
              <Field name="user_api_token" mr={1} mb={0} readOnly large>
                <InputGroup width={300} />
              </Field>
              <Button text="Reset API Token" loading={$users.resettingApiToken} onClick={this.handleResetApiToken} />
            </Flex>

            <Box borderTop="thin" my={2} />

            <Flex my={2}>
              {totpEnabled && (
                <Button
                  text="Disable 2-Factor Authentication"
                  loading={$users.disablingTotp}
                  onClick={this.handleDisableTotp}
                />
              )}
              {!totpEnabled && (
                <Text fontSize={14} muted>
                  2-Factor authentication is disabled for this user
                </Text>
              )}
            </Flex>

            <Box borderTop="thin" my={2} />

            <Heading level={5} mb={2}>
              Layout Preferences
            </Heading>
            <Text large muted mb={2}>
              Advanced settings that override default layout behavior in v4.
            </Text>

            <Box mb={3}>
              <Text fontWeight="bold" mb={2}>
                Show details in
              </Text>
              <RadioGroup
                large
                value={alwaysDockDrawers}
                onChange={(value) => $setup.updateSettings({ layout: { ...layoutSettings, alwaysDockDrawers: value } })}
                options={[
                  {
                    value: false,
                    label: (
                      <FlexColumn>
                        <Text fontWeight="bold" as="div">
                          Drawer
                        </Text>
                        <Text muted>(overlaid on main content)</Text>
                      </FlexColumn>
                    ),
                    type: 'drawer'
                  },
                  {
                    value: true,
                    label: (
                      <FlexColumn>
                        <Text fontWeight="bold" as="div">
                          Dock
                        </Text>
                        <Text muted>(next to main content)</Text>
                      </FlexColumn>
                    ),
                    type: 'dock'
                  }
                ]}
                itemRenderer={DockedItemRenderer}
              />
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>
                Place drawer/dock on the
              </Text>
              <RadioGroup
                large
                value={drawerLocation}
                onChange={(value) => $setup.updateSettings({ layout: { ...layoutSettings, drawerLocation: value } })}
                options={[
                  { value: 'right', label: 'Right' },
                  { value: 'left', label: 'Left' }
                ]}
              />
            </Box>
          </Box>
        )}
      </Suspense>
    );
  }
}

export default UserInfo;
